import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatDateTime, formatErrorDetail } from "@/lib/api";
import { Empty, PageHeader, Stat, Surface } from "@/components/app/Primitives";
import StatusBadge from "@/components/app/StatusBadge";
import EmployeePicker from "@/components/app/EmployeePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, ClipboardCheck, PlayCircle, RotateCcw, Upload, UserPlus, Settings, Plus } from "lucide-react";
import { toast } from "sonner";

function OnboardingTemplates() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", tasks: [{ title: "", category: "documents", required: true, due_offset_days: "" }] });
  const load = () => api.get("/onboarding/plans").then((r) => setPlans(r.data.items || [])).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);
  const addTask = () => setForm({ ...form, tasks: [...form.tasks, { title: "", category: "general", required: true, due_offset_days: "" }] });
  const save = async () => {
    if (!form.name.trim()) return toast.error("Template name is required");
    try {
      await api.post("/onboarding/plans", { ...form, tasks: form.tasks.filter((t) => t.title.trim()).map((t) => ({ ...t, due_offset_days: t.due_offset_days === "" ? null : Number(t.due_offset_days) })) });
      toast.success("Template saved");
      setForm({ name: "", description: "", tasks: [{ title: "", category: "documents", required: true, due_offset_days: "" }] });
      load();
    } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/onboarding/plans/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="h-9 px-3 rounded-md border border-border bg-background text-sm flex items-center gap-2" data-testid="onboarding-templates-btn"><Settings className="size-4" /> Templates</button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Onboarding Templates</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-auto">
          <div className="space-y-1">
            {plans.map((p) => (
              <div key={p.id || p.name} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                <div><span className="font-medium">{p.name}</span> <span className="text-xs text-muted-foreground">· {(p.tasks || []).length} tasks{p.source === "default" ? " · default" : ""}</span></div>
                {p.id && <button className="text-xs text-destructive" onClick={() => del(p.id)}>Delete</button>}
              </div>
            ))}
          </div>
          <div className="rounded-md border border-border p-3">
            <div className="font-semibold text-sm mb-2">New template</div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tpl-name" /></div>
              <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <div className="mt-2"><Label>Tasks</Label>
              {form.tasks.map((t, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mt-1">
                  <Input className="col-span-5" placeholder="Task title" value={t.title} onChange={(e) => setForm({ ...form, tasks: form.tasks.map((x, j) => j === i ? { ...x, title: e.target.value } : x) })} />
                  <Input className="col-span-3" placeholder="Category" value={t.category} onChange={(e) => setForm({ ...form, tasks: form.tasks.map((x, j) => j === i ? { ...x, category: e.target.value } : x) })} />
                  <Input className="col-span-2" type="number" placeholder="Due +days" value={t.due_offset_days} onChange={(e) => setForm({ ...form, tasks: form.tasks.map((x, j) => j === i ? { ...x, due_offset_days: e.target.value } : x) })} />
                  <label className="col-span-2 flex items-center gap-1 text-xs"><input type="checkbox" checked={t.required} onChange={(e) => setForm({ ...form, tasks: form.tasks.map((x, j) => j === i ? { ...x, required: e.target.checked } : x) })} /> req</label>
                </div>
              ))}
              <button className="text-xs text-primary mt-1" onClick={addTask}><Plus className="size-3 inline" /> Add task</button>
            </div>
          </div>
        </div>
        <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={save} data-testid="tpl-save">Save Template</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const catLabel = (s) =>
  String(s || "general").replaceAll("_", " ").replace(/\b\w/g, (m) => m.toUpperCase());

const docTypeForTask = (task) => {
  const map = {
    employment_contract: "employment_contract",
    valid_id: "valid_id",
    tin: "tin",
    sss: "sss",
    philhealth: "philhealth",
    pagibig: "pagibig",
    bank_account: "bank_account",
  };
  return map[task?.key] || "other";
};

const DOCUMENT_REQUIRED_TASK_KEYS = new Set([
  "employment_contract",
  "valid_id",
  "tin",
  "sss",
  "philhealth",
  "pagibig",
  "bank_account",
]);

const requiresVerifiedProof = (task) => DOCUMENT_REQUIRED_TASK_KEYS.has(task?.key);

export default function Onboarding() {
  const [instances, setInstances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [employeeId, setEmployeeId] = useState("");
  const [searchParams] = useSearchParams();

  const load = async () => {
    const [i, e] = await Promise.all([
      api.get("/onboarding/instances"),
      api.get("/hris/employees", { params: { limit: 500 } }),
    ]);
    setInstances(i.data.items || []);
    setEmployees(e.data.items || []);
  };

  const loadDetail = async (empId) => {
    const { data } = await api.get(`/onboarding/employees/${empId}`);
    setDetail(data);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const empId = searchParams.get("employee_id");
    if (!empId) return;

    setEmployeeId(empId);

    api.get("/onboarding/instances")
      .then((res) => {
        const found = (res.data.items || []).find((x) => x.employee_id === empId);
        if (found) {
          setSelected(found);
        } else {
          api.post(`/onboarding/employees/${empId}/start`, {})
            .then((created) => setSelected(created.data))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [searchParams]);

  useEffect(() => {
    if (selected?.employee_id) {
      loadDetail(selected.employee_id).catch(() => setDetail(null));
    }
  }, [selected]);

  const start = async () => {
    if (!employeeId) return toast.error("Select an employee first.");
    try {
      const { data } = await api.post(`/onboarding/employees/${employeeId}/start`, {});
      toast.success("Onboarding started");
      await load();
      setSelected(data);
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail));
    }
  };

  const complete = async (taskId) => {
    try {
      await api.post(`/onboarding/tasks/${taskId}/complete`, {});
      toast.success("Task completed");
      await loadDetail(selected.employee_id);
      await load();
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail));
    }
  };

  const reopen = async (taskId) => {
    try {
      await api.post(`/onboarding/tasks/${taskId}/reopen`, {
        reason: "Reopened from onboarding workspace",
      });
      toast.success("Task reopened");
      await loadDetail(selected.employee_id);
      await load();
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail));
    }
  };

  const uploadTaskDocument = async (task, file) => {
    if (!file || !selected?.employee_id) return;

    const form = new FormData();
    form.append("file", file);
    form.append("doc_type", docTypeForTask(task));
    form.append("onboarding_task_id", task.id);

    try {
      await api.post(`/hris/employees/${selected.employee_id}/documents`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded. Task is pending verification.");
      await loadDetail(selected.employee_id);
      await load();
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail));
    }
  };

  const grouped = useMemo(() => {
    const out = {};
    for (const t of detail?.tasks || []) {
      const k = t.category || "general";
      if (!out[k]) out[k] = [];
      out[k].push(t);
    }
    return out;
  }, [detail]);

  const active = instances.filter((x) => x.status === "active").length;
  const completed = instances.filter((x) => x.status === "completed").length;
  const avgProgress = instances.length
    ? Math.round(instances.reduce((a, b) => a + Number(b.progress || 0), 0) / instances.length)
    : 0;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Onboarding Workspace"
        subtitle="Document, payroll, IT, asset, and orientation readiness for new hires."
        actions={<OnboardingTemplates />}
      />

      <div className="grid gap-3 md:grid-cols-4">
        <Stat label="Onboarding Cases" value={instances.length} icon={UserPlus} tone="primary" />
        <Stat label="Active" value={active} icon={PlayCircle} />
        <Stat label="Completed" value={completed} icon={CheckCircle2} />
        <Stat label="Average Progress" value={`${avgProgress}%`} icon={ClipboardCheck} />
      </div>

      <Surface className="p-4">
        <div className="flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
              Start Onboarding
            </div>
            <EmployeePicker value={employeeId} onChange={setEmployeeId} testId="onb-employee" />
          </div>
          <button className="h-10 px-4 rounded-md bg-primary text-white text-sm" onClick={start}>
            Start
          </button>
        </div>
      </Surface>

      <div className="grid gap-4 lg:grid-cols-3">
        <Surface className="p-4 lg:col-span-1">
          <div className="font-semibold mb-3">New Hire Queue</div>
          <div className="space-y-2">
            {instances.map((x) => (
              <button
                key={x.id}
                onClick={() => setSelected(x)}
                className={`w-full text-left rounded-xl border p-3 transition hover:border-primary ${
                  selected?.id === x.id ? "border-primary bg-primary/5" : "bg-white"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-sm">{x.employee_name}</div>
                  <StatusBadge value={x.status} />
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {x.employee_no} · {x.plan_name}
                </div>
                <div className="mt-3 h-2 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${x.progress || 0}%` }} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{x.progress || 0}% complete</div>
              </button>
            ))}
            {!instances.length && (
              <Empty title="No onboarding cases" subtitle="Start onboarding for a newly hired employee." />
            )}
          </div>
        </Surface>

        <Surface className="p-5 lg:col-span-2">
          {!detail ? (
            <Empty title="Select an onboarding case" subtitle="Open a new hire to view checklist and blockers." />
          ) : (
            <div className="space-y-5">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="text-2xl font-bold">{detail.instance.employee_name}</div>
                  <div className="text-sm text-muted-foreground">
                    {detail.instance.employee_no} · {detail.instance.plan_name}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold">{detail.summary.progress}%</div>
                  <div className="text-xs text-muted-foreground">
                    {detail.summary.ready_for_work
                      ? "Ready for work"
                      : `${detail.summary.blockers.length} blockers remaining`}
                  </div>
                </div>
              </div>

              <div className="h-3 rounded-full bg-secondary overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${detail.summary.progress}%` }} />
              </div>

              {Object.entries(grouped).map(([category, tasks]) => (
                <div key={category}>
                  <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">
                    {catLabel(category)}
                  </div>
                  <div className="space-y-2">
                    {tasks.map((t) => (
                      <div
                        key={t.id}
                        className="rounded-xl border bg-white p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                      >
                        <div>
                          <div className="font-medium text-sm flex items-center gap-2">
                            {t.status === "completed" ? (
                              <CheckCircle2 className="size-4 text-emerald-600" />
                            ) : (
                              <ClipboardCheck className="size-4 text-muted-foreground" />
                            )}
                            {t.title}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Owner: {t.owner_role || "—"} · {t.required ? "Required" : "Optional"}
                            {t.completed_at ? ` · Completed ${formatDateTime(t.completed_at)}` : ""}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <StatusBadge value={t.status} />
                          {t.status === "completed" ? (
                            <button
                              className="h-8 px-3 rounded-md bg-secondary text-xs inline-flex items-center gap-1"
                              onClick={() => reopen(t.id)}
                            >
                              <RotateCcw className="size-3" /> Reopen
                            </button>
                          ) : (
                            <>
                              {t.document_filename && (
                                <span className="text-xs text-muted-foreground max-w-[180px] truncate">
                                  Proof: {t.document_filename}
                                </span>
                              )}

                              <label className="h-8 px-3 rounded-md bg-secondary text-xs inline-flex items-center gap-1 cursor-pointer">
                                <Upload className="size-3" /> Upload Proof
                                <input
                                  type="file"
                                  className="hidden"
                                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                                  onChange={(e) => uploadTaskDocument(t, e.target.files?.[0])}
                                />
                              </label>

                              {requiresVerifiedProof(t) ? (
                                <span className="h-8 px-3 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-xs inline-flex items-center">
                                  {t.document_filename
                                    ? "Pending HR verification"
                                    : "Verified proof required"}
                                </span>
                              ) : (
                                <button
                                  className="h-8 px-3 rounded-md bg-primary text-white text-xs"
                                  onClick={() => complete(t.id)}
                                >
                                  Complete Manually
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Surface>
      </div>
    </div>
  );
}
